import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { DatePicker, Dropdown, Modal, Select, Spin } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { FiltersIcon, ReloadIcon } from '../../../assets';
import {
  getDetailedViewStatusColor,
  getDetailedViewStatusLabel,
} from '../../../pages/MH_Admin/MH_Reporting_Dashboard/detailedViewHelpers';
import { RouteArrow } from './RouteArrow';
import styles from './AlertsViewAllModal.module.css';

const { RangePicker } = DatePicker;

const PAGE_SIZE = 10;
export const VIEW_ALL_PAGE_SIZE = PAGE_SIZE;

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'archived', label: 'Archived' },
  { key: 'trash', label: 'Trash' },
];

const DATE_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Date Range' },
];

const DEFAULT_DATE_PRESET = 'all_time';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
];

const GROUP_ORDER = ['TODAY', 'YESTERDAY', 'EARLIER'];

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="3" r="1.5" fill="#5F5E5A" />
    <circle cx="8" cy="8" r="1.5" fill="#5F5E5A" />
    <circle cx="8" cy="13" r="1.5" fill="#5F5E5A" />
  </svg>
);

const PageChevron = ({ direction = 'left' }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d={direction === 'left' ? 'M8.5 3L4.5 7L8.5 11' : 'M5.5 3L9.5 7L5.5 11'}
      stroke="#5F5E5A"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getRowActionItems = (onAction) => [
  {
    key: 'delete',
    label: 'Delete',
    onClick: ({ domEvent }) => {
      domEvent?.stopPropagation();
      domEvent?.preventDefault();
      onAction('delete');
    },
  },
  { type: 'divider' },
  {
    key: 'archive',
    label: 'Archive',
    onClick: ({ domEvent }) => {
      domEvent?.stopPropagation();
      domEvent?.preventDefault();
      onAction('archive');
    },
  },
];

const matchesDatePreset = (createdAt, preset, customRange) => {
  if (!createdAt) return true;

  const date = dayjs(createdAt);
  const now = dayjs();

  switch (preset) {
    case 'today':
      return date.isSame(now, 'day');
    case 'yesterday':
      return date.isSame(now.subtract(1, 'day'), 'day');
    case 'last_30_days':
      return date.isAfter(now.subtract(30, 'day').startOf('day')) || date.isSame(now.subtract(30, 'day'), 'day');
    case 'this_month':
      return date.isSame(now, 'month');
    case 'custom': {
      const [start, end] = customRange || [];
      if (!start || !end) return true;
      return (
        (date.isSame(start, 'day') || date.isAfter(start.startOf('day'))) &&
        (date.isSame(end, 'day') || date.isBefore(end.endOf('day')))
      );
    }
    case 'all_time':
    default:
      return true;
  }
};

const resolveFolder = (id, trashedIds, archivedIds) => {
  if (trashedIds.includes(id)) return 'trash';
  if (archivedIds.includes(id)) return 'archived';
  return 'inbox';
};

export const AlertsViewAllModal = ({
  visible,
  onClose,
  alerts = [],
  totalCount = 0,
  loading = false,
  page = 1,
  onPageChange,
  sortBy = 'newest',
  onSortByChange,
  onOpen,
}) => {
  const [tab, setTab] = useState('all');
  const [datePreset, setDatePreset] = useState(DEFAULT_DATE_PRESET);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const [trashedIds, setTrashedIds] = useState([]);
  const [archivedIds, setArchivedIds] = useState([]);
  const suppressRowClickRef = useRef(false);

  const items = useMemo(
    () =>
      alerts.map((alert) => ({
        ...alert,
        folder: resolveFolder(alert.id, trashedIds, archivedIds),
      })),
    [alerts, trashedIds, archivedIds]
  );

  useEffect(() => {
    if (visible) {
      setTab('all');
      setDatePreset(DEFAULT_DATE_PRESET);
      setCustomDateRange(null);
      setRangePickerOpen(false);
      setTrashedIds([]);
      setArchivedIds([]);
    }
  }, [visible]);

  const handleRowAction = useCallback((id, action) => {
    // Prevent the closing dropdown click from also triggering the row's onOpen.
    suppressRowClickRef.current = true;
    window.setTimeout(() => {
      suppressRowClickRef.current = false;
    }, 100);

    if (action === 'delete') {
      setTrashedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setArchivedIds((prev) => prev.filter((archivedId) => archivedId !== id));
      return;
    }

    if (action === 'archive') {
      setArchivedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setTrashedIds((prev) => prev.filter((trashedId) => trashedId !== id));
    }
  }, []);

  const handleRowClick = useCallback(
    (alert) => {
      if (suppressRowClickRef.current) return;
      onOpen?.(alert);
    },
    [onOpen]
  );

  const handleDeleteAll = useCallback(() => {
    const inboxIds = items.filter((alert) => alert.folder === 'inbox').map((alert) => alert.id);
    if (inboxIds.length === 0) return;

    setTrashedIds((prev) => [...new Set([...prev, ...inboxIds])]);
    setArchivedIds((prev) => prev.filter((id) => !inboxIds.includes(id)));
    setTab('trash');
    onPageChange?.(1);
  }, [items, onPageChange]);

  const filteredAlerts = useMemo(() => {
    let next = items.filter((alert) => matchesDatePreset(alert.createdAt, datePreset, customDateRange));

    if (tab === 'all') {
      next = next.filter((alert) => alert.folder === 'inbox');
    } else if (tab === 'unread') {
      next = next.filter((alert) => alert.folder === 'inbox' && alert.unread);
    } else if (tab === 'archived') {
      next = next.filter((alert) => alert.folder === 'archived');
    } else if (tab === 'trash') {
      next = next.filter((alert) => alert.folder === 'trash');
    }

    next = [...next].sort((a, b) => {
      const aTime = dayjs(a.createdAt).valueOf();
      const bTime = dayjs(b.createdAt).valueOf();
      return sortBy === 'oldest' ? aTime - bTime : bTime - aTime;
    });

    return next;
  }, [items, tab, sortBy, datePreset, customDateRange]);

  const isUnfilteredAllTab =
    tab === 'all' &&
    datePreset === DEFAULT_DATE_PRESET &&
    items.every((alert) => alert.folder === 'inbox');
  const pageCount = isUnfilteredAllTab
    ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
    : Math.max(1, Math.ceil(Math.max(filteredAlerts.length, 1) / PAGE_SIZE));
  const pageItems = filteredAlerts;

  const groupedAlerts = useMemo(
    () =>
      GROUP_ORDER.map((group) => ({
        group,
        items: pageItems.filter((alert) => alert.group === group),
      })).filter(({ items: groupItems }) => groupItems.length > 0),
    [pageItems]
  );

  const showingCount = filteredAlerts.length === 0 ? 0 : pageItems.length;
  const footerTotal = isUnfilteredAllTab ? totalCount : filteredAlerts.length;
  const rangeStart = showingCount === 0 ? 0 : isUnfilteredAllTab ? (page - 1) * PAGE_SIZE + 1 : 1;
  const rangeEnd = showingCount === 0 ? 0 : rangeStart + showingCount - 1;

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    onPageChange?.(1);
  };

  const handleRefreshFilters = () => {
    setDatePreset(DEFAULT_DATE_PRESET);
    setCustomDateRange(null);
    setRangePickerOpen(false);
    onSortByChange?.('newest');
    onPageChange?.(1);
  };

  const headerMenuItems = [
    {
      key: 'delete-all',
      label: 'Delete All',
      onClick: handleDeleteAll,
      disabled: items.every((alert) => alert.folder !== 'inbox'),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={980}
      destroyOnClose
      className={styles.modal}
      wrapClassName={styles.modalWrap}
      closable
      closeIcon={
        <svg
          className={styles.closeIcon}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5"
            stroke="#111827"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      }
      maskStyle={{ backgroundColor: 'rgba(44, 44, 42, 0.45)' }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.headerLeft}>
              <h2 className={styles.title}>Alerts & Updates</h2>
              <div className={styles.tabs} role="tablist" aria-label="Alert folders">
                {TABS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={tab === key}
                    className={classnames(styles.tab, { [styles.tabActive]: tab === key })}
                    onClick={() => handleTabChange(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.headerRight}>
              <div className={styles.filterBar}>
                <div className={styles.filtersLabel}>
                  <FiltersIcon width={16} height={16} />
                  <span>Filters</span>
                </div>

                <div className={styles.filterControl}>
                  <span
                    className={styles.filterLabel}
                    role={datePreset === 'custom' ? 'button' : undefined}
                    tabIndex={datePreset === 'custom' ? 0 : undefined}
                    title={datePreset === 'custom' ? 'Back to date presets' : undefined}
                    onClick={
                      datePreset === 'custom'
                        ? () => {
                            setDatePreset(DEFAULT_DATE_PRESET);
                            setCustomDateRange(null);
                            setRangePickerOpen(false);
                            onPageChange?.(1);
                          }
                        : undefined
                    }
                  >
                    Date
                  </span>
                  {datePreset === 'custom' ? (
                    <RangePicker
                      value={customDateRange}
                      open={rangePickerOpen}
                      onOpenChange={setRangePickerOpen}
                      onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) {
                          setCustomDateRange(dates);
                        } else {
                          setCustomDateRange(null);
                          setDatePreset(DEFAULT_DATE_PRESET);
                        }
                        onPageChange?.(1);
                      }}
                      variant="borderless"
                      size="small"
                      className={styles.filterRangePicker}
                      format="MMM D, YYYY"
                      separator=" - "
                      suffixIcon={<DownOutlined className={styles.selectChevron} />}
                      allowClear
                      getPopupContainer={(trigger) =>
                        trigger?.closest(`.${styles.content}`) || document.body
                      }
                    />
                  ) : (
                    <Select
                      value={datePreset}
                      onChange={(value) => {
                        setDatePreset(value);
                        if (value === 'custom') {
                          setCustomDateRange(null);
                          setRangePickerOpen(true);
                        }
                        onPageChange?.(1);
                      }}
                      options={DATE_OPTIONS}
                      variant="borderless"
                      size="small"
                      className={styles.filterSelect}
                      suffixIcon={<DownOutlined className={styles.selectChevron} />}
                      popupMatchSelectWidth={false}
                      getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    />
                  )}
                </div>

                <div className={styles.filterControl}>
                  <span className={styles.filterLabel}>Sort by</span>
                  <Select
                    value={sortBy}
                    onChange={(value) => {
                      onSortByChange?.(value);
                    }}
                    options={SORT_OPTIONS}
                    variant="borderless"
                    size="small"
                    className={styles.filterSelect}
                    suffixIcon={<DownOutlined className={styles.selectChevron} />}
                    popupMatchSelectWidth={false}
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                  />
                </div>

                <button
                  type="button"
                  className={styles.refreshBtn}
                  aria-label="Refresh alerts"
                  onClick={handleRefreshFilters}
                >
                  <ReloadIcon width={16} height={16} fill="#2FA770" stroke="transparent" />
                </button>
              </div>

              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                overlayClassName={styles.rowActionsDropdown}
                menu={{ items: headerMenuItems }}
                getPopupContainer={(trigger) =>
                  trigger?.closest(`.${styles.content}`) || document.body
                }
              >
                <button type="button" className={styles.moreBtn} aria-label="More options">
                  <MoreIcon />
                </button>
              </Dropdown>
            </div>
          </div>
        </header>

        {/* <NotificationScrollArea className={styles.list}>
          <Spin spinning={loading} className={styles.listSpin} size="large">
            {groupedAlerts.length === 0 ? (
              <div className={styles.emptyState}>{loading ? null : 'No alerts to show'}</div>
            ) : (
              groupedAlerts.map(({ group, items: groupItems }) => (
                <section key={group}>
                  <div className={styles.groupHeader}>{group}</div>
                  <ul className={styles.groupList}>
                    {groupItems.map((alert) => (
                      <li
                        key={alert.id}
                        className={styles.row}
                        onClick={() => handleRowClick(alert)}
                      >
                        <div className={styles.rowMain}>
                          <p className={styles.rowTitle}>{alert.title}</p>

                          {(alert.pickupAddress || alert.dropoffAddress) && (
                            <div className={styles.rowRoute}>
                              {alert.pickupAddress && (
                                <span className={styles.routeAddress}>{alert.pickupAddress}</span>
                              )}
                              {alert.pickupAddress && alert.dropoffAddress && (
                                <RouteArrow className={styles.routeArrow} />
                              )}
                              {alert.dropoffAddress && (
                                <span className={styles.routeAddress}>{alert.dropoffAddress}</span>
                              )}
                            </div>
                          )}

                          {alert.meta && <p className={styles.rowMeta}>{alert.meta}</p>}

                          {alert.description && (
                            <p className={styles.rowDescription}>{alert.description}</p>
                          )}
                        </div>

                        <div
                          className={styles.rowAside}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {alert.unread && (
                            <span className={styles.unreadDot} aria-label="Unread" />
                          )}
                          <div className={styles.rowSide}>
                            {alert.status && (
                              <span
                                className={styles.statusPill}
                                style={{
                                  color: getDetailedViewStatusColor(alert.status),
                                  borderColor: getDetailedViewStatusColor(alert.status),
                                }}
                              >
                                {getDetailedViewStatusLabel(alert.status)}
                              </span>
                            )}
                            <span className={styles.rowTimestamp}>{alert.timestamp}</span>
                            <Dropdown
                              trigger={['click']}
                              placement="bottomRight"
                              overlayClassName={styles.rowActionsDropdown}
                              menu={{
                                items: getRowActionItems((action) =>
                                  handleRowAction(alert.id, action)
                                ),
                              }}
                              getPopupContainer={(trigger) =>
                                trigger?.closest(`.${styles.content}`) || document.body
                              }
                            >
                              <button
                                type="button"
                                className={styles.rowMoreBtn}
                                aria-label="Row actions"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <MoreIcon />
                              </button>
                            </Dropdown>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            )}
          </Spin>
        </NotificationScrollArea> */}

        <footer className={styles.footer}>
          <span className={styles.showing}>
            {showingCount === 0 ? `Showing 0 of ${footerTotal}` : `Showing ${rangeStart}-${rangeEnd} of ${footerTotal}`}
          </span>
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageBtn}
              aria-label="Previous page"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange?.(Math.max(1, page - 1))}
            >
              <PageChevron direction="left" />
            </button>
            <button
              type="button"
              className={styles.pageBtn}
              aria-label="Next page"
              disabled={page >= pageCount || loading}
              onClick={() => onPageChange?.(Math.min(pageCount, page + 1))}
            >
              <PageChevron direction="right" />
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

AlertsViewAllModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  alerts: PropTypes.array,
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  page: PropTypes.number,
  onPageChange: PropTypes.func,
  sortBy: PropTypes.oneOf(['newest', 'oldest']),
  onSortByChange: PropTypes.func,
  onOpen: PropTypes.func,
};
